import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
    ActivityLevelMeta,
    BODY_LABEL,
    TreatFrequencyMeta,
    safeStr,
    buildDogForStorage,
    HEALTH_ISSUE_NONE,
    HealthIssue,
} from "@/helpers/enums";
import {
    calculateDailyIntakeFromForm,
    type DogFormLike,
} from "@/helpers/calorieCalculator";

import { useDog } from "@/state/useDog";
import { useSendQuizToKlaviyo } from "@/domains/klaviyo/klaviyo";
import { quizTestTags } from "@/experiments/quizTests";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";

type Props = {
    dogs: {
        id: string;
        form: any;
    }[];
    activeDogId: string | null;
};

type StatCardProps = {
    icon: string;
    label: string;
    value: string | number;
};

/** Figma profile stat card — white rounded tile, brand icon, RL Aqva value + grey label. */
const StatCard = ({ icon, label, value }: StatCardProps) => (
    <Box
        sx={{
            bgcolor: QUIZ.color.white,
            borderRadius: QUIZ.radius.card,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            pl: "12px",
            pr: "16px",
            py: "12px",
            width: "100%",
            boxSizing: "border-box",
        }}
    >
        <Box
            component="img"
            src={icon}
            alt=""
            sx={{ width: { xs: 38, md: 44 }, height: { xs: 38, md: 44 }, objectFit: "contain", flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 17, md: 18 },
                    color: QUIZ.color.ink,
                    lineHeight: 1.1,
                }}
            >
                {value}
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: 13, color: QUIZ.color.statLabel }}>
                {label}
            </Typography>
        </Box>
    </Box>
);

/**
 * Issue #54 — the reassurance moment the health question exists for: telling
 * the owner their dog's condition was taken into account. Renders only when a
 * real issue was picked ("No health issues" and blank stay silent).
 */
const HealthReassuranceCard = ({ dogName, issues }: { dogName: string; issues: string[] }) => {
    if (!issues.length) return null;

    // List every selected issue by name — "skin issues (and 2 more)" read as
    // impersonal and undercut the personalisation the card exists for.
    // "Other" alone reads badly lowercased, so it maps to a generic phrase.
    const named = issues.map((i) =>
        i === HealthIssue.OTHER ? "the other issues you noted" : i.toLowerCase()
    );
    const issueList =
        named.length === 1
            ? named[0]
            : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;

    return (
        <Box
            sx={{
                bgcolor: QUIZ.color.white,
                borderRadius: QUIZ.radius.card,
                px: "16px",
                py: "14px",
                mt: { xs: "16px", md: "23px" },
                width: "100%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                textAlign: "left",
            }}
        >
            <Typography component="span" aria-hidden sx={{ fontSize: 20, lineHeight: 1.2 }}>
                🩺
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: { xs: 14, md: 15 },
                    color: QUIZ.color.ink,
                    lineHeight: 1.45,
                }}
            >
                {dogName} has <strong>{issueList}</strong>.
            </Typography>
        </Box>
    );
};

function DogSummary({ dogId }: { dogId: string }) {
    const { form = {}, update } = useDog(dogId);
    const sendQuizToKlaviyo = useSendQuizToKlaviyo();

    const normalizedForm: DogFormLike = useMemo(
        () => ({
            ...(form || {}),
            years:
                form?.years !== undefined && form.years !== "" ? Number(form.years) : undefined,
            months:
                form?.months !== undefined && form.months !== "" ? Number(form.months) : undefined,
            weightKg:
                form?.weightKg !== undefined && form.weightKg !== ""
                    ? Number(form.weightKg)
                    : undefined,
        }),
        [form]
    );

    const {
        name,
        weightKg,
        bodyCondition,
        treatFrequency,
        activityLevel,
    } = form;

    const calc = useMemo(() => calculateDailyIntakeFromForm(normalizedForm, { kcalPerGram: 1.65 }), [normalizedForm]);
    const { dailyCalories, gramsPerDay } = calc;
    const activityLevelMeta = ActivityLevelMeta[activityLevel as keyof typeof ActivityLevelMeta];
    useEffect(() => {
        if (form.dailyCalories !== dailyCalories || form.dailyGrams !== gramsPerDay) {
            update({
                dailyCalories,
                dailyGrams: gramsPerDay,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dailyCalories, gramsPerDay]);

    const yearsNum: number =
        normalizedForm.years !== undefined ? Number(normalizedForm.years) : 0;
    const monthsNum: number =
        normalizedForm.months !== undefined ? Number(normalizedForm.months) : 0;

    const ageLabel =
        yearsNum || monthsNum
            ? `${yearsNum ? `${yearsNum} year${yearsNum > 1 ? "s" : ""}` : ""} ${monthsNum ? `${monthsNum} month${monthsNum > 1 ? "s" : ""}` : ""
                }`.trim()
            : "N/A";

    function stableStringify(obj: any) {
        return JSON.stringify(obj, Object.keys(obj).sort());
    }

    async function sha1(input: string) {
        // Browser crypto
        const enc = new TextEncoder().encode(input);
        const buf = await crypto.subtle.digest("SHA-1", enc);
        return Array.from(new Uint8Array(buf))
            .map(b => b.toString(16).padStart(2, "0"))
            .join("");
    }

    function normList(x: unknown): string[] {
        if (!Array.isArray(x)) return [];
        return x.map(String).map(s => s.trim()).filter(Boolean).sort();
    }

    function stableStringifyDeep(obj: any) {
        const seen = new WeakSet();
        const sorter = (x: any): any => {
            if (x && typeof x === "object") {
                if (seen.has(x)) return null;
                seen.add(x);

                if (Array.isArray(x)) return x.map(sorter);
                return Object.keys(x).sort().reduce((acc: any, k) => {
                    acc[k] = sorter(x[k]);
                    return acc;
                }, {});
            }
            return x;
        };
        return JSON.stringify(sorter(obj));
    }

    const watchJson = useMemo(() => {
        const email = String(form?.ownerEmail || "").trim().toLowerCase();

        // IMPORTANT: include ALL fields you care about
        const dog = buildDogForStorage({
            ...form,
            id: dogId,
            name: safeStr(form?.name) || "Dog",
            breed: safeStr(form?.breed) || null,
            bodyCondition: bodyCondition || undefined,
            activityLevel: activityLevel || undefined,
            treatFrequency: treatFrequency || undefined,
            dailyGrams: gramsPerDay,
            dailyCalories,
            ageYears: yearsNum,
            ageMonths: monthsNum,
            allergies: normList(form?.allergies),
            healthIssues: normList(form?.healthIssues),
            weightKg:
                form?.weightKg !== undefined && form.weightKg !== ""
                    ? Number(form.weightKg)
                    : undefined,
        });

        return stableStringifyDeep({
            email,
            ownerName: safeStr(form?.ownerName) || null,
            quizId: safeStr(form?.quizId) || "paws-quiz",
            marketingOptIn: form?.marketingOptIn === true,
            dog,
        });
    }, [
        dogId,
        gramsPerDay,
        dailyCalories,
        yearsNum,
        monthsNum,

        form?.ownerEmail,
        form?.ownerName,
        form?.quizId,
        form?.marketingOptIn,

        form?.name,
        form?.breed,
        form?.sex,
        form?.reproduction,
        form?.bodyCondition,
        form?.activityLevel,
        form?.treatFrequency,
        form?.weightKg,
        form?.selectedPackSize,
        form?.selectedPlanDays,

        // arrays: use JSON string of normalized version as deps
        JSON.stringify(normList(form?.allergies)),
        JSON.stringify(normList(form?.healthIssues)),
    ]);
    function dogWatchKey(form: any, dogId: string, gramsPerDay: number, dailyCalories: number, yearsNum: number, monthsNum: number) {
        const dog = buildDogForStorage({
            ...form,
            id: dogId,
            dailyGrams: gramsPerDay,
            dailyCalories,
            ageYears: yearsNum,
            ageMonths: monthsNum,
            // harden arrays in case state mutates in-place
            allergies: normList(form?.allergies),
            healthIssues: normList(form?.healthIssues),
        });

        // only include fields you want to trigger updates
        return stableStringify({
            email: String(form?.ownerEmail || "").trim().toLowerCase(),
            ownerName: safeStr(form?.ownerName) || null,
            quizId: safeStr(form?.quizId) || "paws-quiz",
            marketingOptIn: form?.marketingOptIn === true,
            dog,
        });
    }

    useEffect(() => {
        if (!dailyCalories || !gramsPerDay) return;

        const email = String(form?.ownerEmail || "").trim().toLowerCase();
        if (!email) return;

        let cancelled = false;

        const t = window.setTimeout(() => {
            (async () => {
                const hash = await sha1(watchJson);

                const storageKey = `klaviyo_quiz_last_hash:${dogId}:${email}`;
                if (sessionStorage.getItem(storageKey) === hash) return;

                const payload = {
                    email,
                    ownerName: safeStr(form?.ownerName) || undefined,
                    quizId: safeStr(form?.quizId) || "paws-quiz",
                    source: "paws-quiz",
                    marketingOptIn: form?.marketingOptIn === true,
                    // Quiz tests (#51): arm on the Klaviyo profile so quiz
                    // takers who never buy can still be cut by arm.
                    quizTests: quizTestTags(window.__PAWS_EXPERIMENTS__),
                    dogs: [
                        buildDogForStorage({
                            ...form,
                            id: dogId,
                            dailyGrams: gramsPerDay,
                            dailyCalories,
                            ageYears: yearsNum,
                            ageMonths: monthsNum,
                            allergies: normList(form?.allergies),
                            healthIssues: normList(form?.healthIssues),
                        }),
                    ],
                };

                try {
                    await sendQuizToKlaviyo(payload);
                    if (cancelled) return;
                    sessionStorage.setItem(storageKey, hash);
                    // Klaviyo sync complete
                } catch (err: any) {
                    if (cancelled) return;
                    console.error("[Klaviyo] Failed to send quiz update", err?.message ?? err);
                }
            })();
        }, 600);

        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [watchJson, sendQuizToKlaviyo, dogId, gramsPerDay, dailyCalories]);

    const bodyLabel =
        bodyCondition && BODY_LABEL[bodyCondition as keyof typeof BODY_LABEL]
            ? BODY_LABEL[bodyCondition as keyof typeof BODY_LABEL]
            : bodyCondition || "N/A";
    const treatLabel =
        treatFrequency && TreatFrequencyMeta[treatFrequency as keyof typeof TreatFrequencyMeta]
            ? TreatFrequencyMeta[treatFrequency as keyof typeof TreatFrequencyMeta].label
            : treatFrequency || "N/A";
    const activityShort = activityLevelMeta
        ? activityLevelMeta.max === Infinity
            ? `${activityLevelMeta.min}+ mins`
            : `${activityLevelMeta.min}-${activityLevelMeta.max} mins`
        : "N/A";

    return (
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: 536 }, mx: "auto", textAlign: "center" }}>
            {/* Heading */}
            <Typography sx={{ fontFamily: QUIZ.font.label, fontSize: { xs: 16, md: 19 }, color: QUIZ.color.ink }}>
                Your dog's profile
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 30, md: 50 },
                    color: QUIZ.color.ink,
                    lineHeight: 1.1,
                    mt: "8px",
                }}
            >
                {name || "Your Dog"}
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 16, md: 18 }, color: QUIZ.color.ink, mt: { xs: "8px", md: "14px" } }}>
                Daily recommended calories
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 20, md: 22 },
                    color: QUIZ.color.ink,
                    mt: "4px",
                }}
            >
                {dailyCalories} kcal
            </Typography>

            {/* Stat grid — 2 × 3 on mobile (Figma 12), 3 × 2 on desktop (Figma 1586:10923) */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" },
                    gap: { xs: "16px", md: "23px" },
                    mt: { xs: "20px", md: "32px" },
                    width: "100%",
                }}
            >
                <StatCard
                    icon={quizAsset("icon-weight")}
                    label="Weight"
                    value={weightKg != null && weightKg !== "" ? `${weightKg} kg` : "N/A"}
                />
                <StatCard icon={quizAsset("icon-ideal")} label="Body type" value={bodyLabel} />
                <StatCard icon={quizAsset("icon-age")} label="Age" value={ageLabel} />
                <StatCard icon={quizAsset("icon-treat")} label="Treats" value={treatLabel} />
                <StatCard
                    icon={quizAsset("icon-active")}
                    label="Activity per day"
                    value={activityShort}
                />
                <StatCard icon={quizAsset("icon-food")} label="Daily grams" value={`${gramsPerDay} g`} />
            </Box>

            {/* Issue #54: health-issues reassurance */}
            <HealthReassuranceCard
                dogName={name || "your dog"}
                issues={(form?.healthIssues ?? []).filter((h: string) => h !== HEALTH_ISSUE_NONE)}
            />
        </Box>
    );
}

export default function ResultsStepSummary({ dogs }: Props) {
    if (!dogs || dogs.length === 0) return null;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 3, md: 6 },

                // ✅ mobile padding so the Paper never touches edges
                px: { xs: 1.5, sm: 2, md: 0 },

                // ✅ guard against accidental horizontal scroll
                overflowX: "hidden",
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            {dogs.map((dog) => (
                <DogSummary key={dog.id} dogId={dog.id} />
            ))}
        </Box>
    );
}